import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

type Filter = 'pending' | 'resolved' | 'all';

export default function AdminReportsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('pending');
  const [page, setPage]     = useState(1);

  const { data, isLoading } = useQuery(
    ['admin-reports', filter, page],
    () => apiClient.get<any>('/admin/reports', { params: { filter, page, limit: 25 } }),
    { keepPreviousData: true }
  );

  const resolveMut = useMutation(
    ({ id, resolution }: { id: string; resolution: string }) =>
      apiClient.post(`/admin/reports/${id}/resolve`, { resolution }),
    { onSuccess: () => { qc.invalidateQueries('admin-reports'); toast.success('Report resolved'); } }
  );

  const [resolving, setResolving] = useState<{ id: string; resolution: string } | null>(null);

  const reports: any[] = data?.reports ?? [];
  const total: number  = data?.total ?? 0;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">Reports</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6">{total} reports</p>

      <div className="flex gap-3 mb-4">
        {(['pending','resolved','all'] as Filter[]).map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-zinc-400'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_,i) => <div key={i} className="h-16 rounded bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}</div>
        ) : reports.length === 0 ? (
          <div className="p-12 text-center text-gray-400 dark:text-zinc-600">
            <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>No {filter} reports</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-zinc-800">
            {reports.map(r => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_resolved ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}>
                        {r.is_resolved ? 'Resolved' : 'Pending'}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-zinc-600 capitalize">{r.target_type}</span>
                    </div>
                    <p className="font-medium text-sm mb-1">{r.reason}</p>
                    <p className="text-xs text-gray-500 dark:text-zinc-500">
                      By {r.reporter_username} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                    {r.resolution && (
                      <p className="mt-2 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2">
                        Resolution: {r.resolution}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.target_type === 'listing' && (
                      <Link to={`/listings/${r.target_id}`} target="_blank" className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500">
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    )}
                    {!r.is_resolved && (
                      <button onClick={() => setResolving({ id: r.id, resolution: '' })}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors">
                        <CheckCircle className="h-4 w-4" /> Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-zinc-800 text-sm text-gray-500 dark:text-zinc-500">
          <span>{total} total</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Prev</button>
            <span className="px-3 py-1">Page {page}</span>
            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      {/* Resolve modal */}
      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setResolving(null)}>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 w-96 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-4">Resolve Report</h3>
            <textarea value={resolving.resolution} onChange={e => setResolving(r => r ? { ...r, resolution: e.target.value } : null)}
              placeholder="Describe the action taken (optional)…" rows={3}
              className="w-full border border-gray-200 dark:border-zinc-700 rounded-xl px-3 py-2 text-sm mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-zinc-800" />
            <div className="flex gap-3">
              <button onClick={() => setResolving(null)} className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-zinc-700 text-sm font-medium">Cancel</button>
              <button onClick={() => { resolveMut.mutate(resolving); setResolving(null); }} className="flex-1 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
